import {layersApi} from "@dv5/api"
import type {ErrorProcessor} from "@dv5/api"
import {Button} from "@dv5/components"
import {DvLayer} from "@dv5/domain"
import type {Widget} from "@dv5/domain"
import {FC, useEffect, useRef, useState} from "react"
import {getWidgetText, getWidgetTypeTitle} from "../services/widgetText"
import './MoveWidgetsToLayersDialog.less'

interface Props {
    isOpen: boolean
    onClose: () => void
    onMove: (layerIds: string[]) => void
    additionalId: string
    additionalType: string
    widgets: Widget[]
}

export const MoveWidgetsToLayersDialog: FC<Props> = ({
    isOpen,
    onClose,
    onMove,
    additionalId,
    additionalType,
    widgets,
}) => {
    const [layers, setLayers] = useState<DvLayer[]>([])
    const [selectedLayers, setSelectedLayers] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const dialogRef = useRef<HTMLDivElement>(null)
    const loadedRef = useRef(false)

    useEffect(() => {
        if (!isOpen) {
            setSelectedLayers([])
            loadedRef.current = false
            return
        }
        if (loadedRef.current) return
        loadedRef.current = true
        setIsLoading(true)
        const noop: ErrorProcessor = {displayError: () => {}, displayStep: () => {}}
        layersApi.loadLayers(additionalId, additionalType, noop, (loadedLayers) => {
            setLayers(loadedLayers)
            setIsLoading(false)
        }).catch(console.error)
    }, [isOpen, additionalId, additionalType])

    if (!isOpen) {
        return null
    }

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onClose()
        }
    }

    const toggleLayer = (layerId: string) => {
        setSelectedLayers(prev =>
            prev.includes(layerId)
                ? prev.filter(id => id !== layerId)
                : [...prev, layerId]
        )
    }

    const handleMove = () => {
        onMove(selectedLayers)
        onClose()
    }

    return (
        <div className="move-widgets-to-layers-dialog-overlay" onClick={handleOverlayClick}>
            <div className="move-widgets-to-layers-dialog" ref={dialogRef}>
                <h3>Select Layers</h3>
                {widgets.length > 0 && (
                    <div className="move-widgets-to-layers-affected">
                        <div className="move-widgets-to-layers-affected-title">Widgets to move</div>
                        {widgets.map(widget => (
                            <div key={widget.id} className="move-widgets-to-layers-affected-item">
                                <span className="move-widgets-to-layers-affected-type">{getWidgetTypeTitle(widget.type)}</span>
                                <span className="move-widgets-to-layers-affected-text">{getWidgetText(widget).trim() || widget.id}</span>
                            </div>
                        ))}
                    </div>
                )}
                {isLoading ? (
                    <div className="move-widgets-to-layers-loading">Loading layers...</div>
                ) : layers.length === 0 ? (
                    <div className="move-widgets-to-layers-empty">No layers found</div>
                ) : (
                    <div className="move-widgets-to-layers-list">
                        {layers.map(layer => (
                            <label
                                key={layer.id}
                                className={[
                                    'move-widgets-to-layers-item',
                                    selectedLayers.includes(layer.id) ? 'selected' : '',
                                    layer.obsoleted ? 'obsoleted' : '',
                                ].filter(Boolean).join(' ')}
                                onMouseDown={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    toggleLayer(layer.id)
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedLayers.includes(layer.id)}
                                    onChange={() => {}}
                                />
                                <div className="move-widgets-to-layers-item-info">
                                    <span className="move-widgets-to-layers-item-title">{layer.title}</span>
                                    <span className="move-widgets-to-layers-item-type">{layer.typeTitle}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                )}
                <div className="move-widgets-to-layers-buttons">
                    <Button title="Cancel" onClick={onClose} className="cancel-dialog-button" />
                    <Button title="Move" onClick={handleMove} className="move-dialog-button" />
                </div>
            </div>
        </div>
    )
}
